import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Copy, QrCode, Settings, Check } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HospitalSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    background_color: '#ffffff',
    font_color: '#000000',
    font_family: 'Inter',
    slug: ''
  });
  const [hospitalId, setHospitalId] = useState(null);
  const [lastSlugUpdate, setLastSlugUpdate] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      // Get hospital ID from user or first hospital
      const hospitalsRes = await axiosInstance.get('/hospitals');
      const hospital = hospitalsRes.data[0];
      
      if (!hospital) {
        toast.error('No hospital found');
        return;
      }

      setHospitalId(hospital.id);
      
      const res = await axiosInstance.get(`/hospitals/${hospital.id}/settings`);
      setSettings(res.data);
      setLastSlugUpdate(res.data.last_slug_update);
      
      // Generate QR code
      const bookingUrl = `${window.location.origin}/book/${res.data.slug}`;
      const qrCode = await QRCode.toDataURL(bookingUrl);
      setQrCodeUrl(qrCode);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSave = async () => {
    if (!hospitalId) return;
    
    try {
      await axiosInstance.patch(`/hospitals/${hospitalId}/settings`, settings);
      toast.success('Settings updated successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/book/${settings.slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('URL copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.download = `${settings.slug}-qr-code.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  const canUpdateSlug = () => {
    if (!lastSlugUpdate) return true;
    const lastUpdate = new Date(lastSlugUpdate);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return lastUpdate < oneMonthAgo;
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-manrope font-bold mb-2">Hospital Settings</h1>
          <p className="text-slate-600">Customize your booking page appearance</p>
        </div>

        {/* Booking URL Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Booking Page URL</CardTitle>
            <CardDescription>Share this URL with your patients</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/book/${settings.slug}`}
                readOnly
                className="flex-1"
              />
              <Button onClick={copyUrl} variant="outline">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            {qrCodeUrl && (
              <div className="flex flex-col items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                <Button onClick={downloadQR} variant="outline">
                  <QrCode className="h-4 w-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* URL Slug */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Custom URL Slug</CardTitle>
            <CardDescription>
              Can be changed once per month
              {lastSlugUpdate && ` • Last updated: ${new Date(lastSlugUpdate).toLocaleDateString()}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Slug</Label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 bg-slate-100 border border-r-0 rounded-l-lg text-sm text-slate-600">
                  /book/
                </span>
                <Input
                  value={settings.slug}
                  onChange={(e) => setSettings({...settings, slug: e.target.value})}
                  disabled={!canUpdateSlug()}
                  placeholder="your-hospital-name"
                  className="rounded-l-none"
                />
              </div>
              {!canUpdateSlug() && (
                <p className="text-xs text-amber-600">
                  Slug can only be changed once per month
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Appearance Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Customize the look of your booking page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings.background_color}
                    onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                    className="w-20 h-10"
                  />
                  <Input
                    value={settings.background_color}
                    onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                    placeholder="#ffffff"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Font Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings.font_color}
                    onChange={(e) => setSettings({...settings, font_color: e.target.value})}
                    className="w-20 h-10"
                  />
                  <Input
                    value={settings.font_color}
                    onChange={(e) => setSettings({...settings, font_color: e.target.value})}
                    placeholder="#000000"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Font Family</Label>
              <Select
                value={settings.font_family}
                onValueChange={(value) => setSettings({...settings, font_family: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inter">Inter (Default)</SelectItem>
                  <SelectItem value="Roboto">Roboto</SelectItem>
                  <SelectItem value="Open Sans">Open Sans</SelectItem>
                  <SelectItem value="Lato">Lato</SelectItem>
                  <SelectItem value="Montserrat">Montserrat</SelectItem>
                  <SelectItem value="Poppins">Poppins</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            <div className="mt-6 p-6 rounded-lg border-2" style={{
              backgroundColor: settings.background_color,
              color: settings.font_color,
              fontFamily: settings.font_family
            }}>
              <h3 className="text-2xl font-bold mb-2">Preview</h3>
              <p>This is how your booking page will look with these settings.</p>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} size="lg">
          <Settings className="h-4 w-4 mr-2" />
          Save Settings
        </Button>
      </div>
    </AdminLayout>
  );
}
